import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import HTMLFlipBook from "react-pageflip";
import { FaDownload, FaExternalLinkAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../style/menu.css";

/* Renders a menu as a 3D page-flip book from pre-rendered page images
   (see scripts/render-menus.js). The PDF is kept only for View/Download. */
function MenuFlipBook({ name, pdfUrl, label, downloadName }) {
  const { t } = useTranslation();
  const bookRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [ratio, setRatio] = useState(1.414);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  // Track viewport width so we can switch between single page (mobile)
  // and a two-page open book (desktop) without reloading the images.
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPages([]);

    fetch(`/menus/${name}/manifest.json`)
      .then((r) => {
        if (!r.ok) throw new Error("manifest");
        return r.json();
      })
      .then((m) => {
        if (cancelled) return;
        setRatio(m.ratio || 1.414);
        setPages(m.pages.map((p) => `/menus/${name}/${p}`));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [name]);

  // Desktop (>=1024px) shows an open two-page spread; mobile shows one page.
  const isDesktop = vw >= 1024;
  const pageW = isDesktop
    ? Math.min(440, Math.floor((vw - 220) / 2))
    : Math.min(440, vw - 60);
  const dims = { w: pageW, h: Math.round(pageW * ratio) };

  const flip = (dir) => {
    const api = bookRef.current?.pageFlip?.();
    if (!api) return;
    dir < 0 ? api.flipPrev() : api.flipNext();
  };

  return (
    <div className="flipbook-wrap">
      {status === "loading" && <div className="flipbook-loading">{t("menu.loading")}</div>}
      {status === "error" && <div className="flipbook-loading">{t("menu.error")}</div>}

      {status === "ready" && (
        <>
          <button className="flip-nav flip-nav-left" onClick={() => flip(-1)} aria-label="Previous page">
            <FaChevronLeft />
          </button>

          <HTMLFlipBook
            key={`${isDesktop ? "spread" : "single"}-${dims.w}`}
            ref={bookRef}
            width={dims.w}
            height={dims.h}
            size="fixed"
            minWidth={200}
            maxWidth={600}
            minHeight={300}
            maxHeight={850}
            showCover={!isDesktop}
            usePortrait={!isDesktop}
            maxShadowOpacity={0.5}
            mobileScrollSupport={true}
            className="flipbook"
          >
            {pages.map((src, i) => (
              <div className="flip-page" key={i}>
                <img src={src} alt={`${label} — ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </HTMLFlipBook>

          <button className="flip-nav flip-nav-right" onClick={() => flip(1)} aria-label="Next page">
            <FaChevronRight />
          </button>
        </>
      )}

      <div className="menu-actions">
        <a className="btn-solid" href={pdfUrl} target="_blank" rel="noopener noreferrer">
          <FaExternalLinkAlt /> {t("menu.view")}
        </a>
        <a className="btn-outline" href={pdfUrl} download={downloadName}>
          <FaDownload /> {t("menu.download")}
        </a>
      </div>
    </div>
  );
}

const MENU = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "sq")
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "sq";

  return (
    <div className="menu-page">
      <section className="menu-block">
        <h2 className="menu-title">{t("menu.restaurantTitle")}</h2>
        <MenuFlipBook
          name={`restaurant-menu-${lang}`}
          pdfUrl={`/menus/restaurant-menu-${lang}.pdf`}
          label={t("menu.restaurantTitle")}
          downloadName={`Step-Arena-Restaurant-Menu-${lang.toUpperCase()}.pdf`}
        />
      </section>

      <section className="menu-block">
        <h2 className="menu-title">{t("menu.lobbyTitle")}</h2>
        <MenuFlipBook
          name={`lobby-menu-${lang}`}
          pdfUrl={`/menus/lobby-menu-${lang}.pdf`}
          label={t("menu.lobbyTitle")}
          downloadName={`Step-Arena-Lobby-Menu-${lang.toUpperCase()}.pdf`}
        />
      </section>
    </div>
  );
};

export default MENU;
